import { isPlatformServer } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from "@angular/core";
import { BehaviorSubject, catchError, lastValueFrom, Observable, of, tap } from "rxjs";
import { GigsumoConstants } from "../helpers/gigsumoConstants";
import { ApiService } from "./api.service";
import { AppSettings } from "./AppSettings";

const JOBS_KEY = makeStateKey('jobs-data');
@Injectable({
    providedIn: 'root',
  })

export class GigsumoService {
  constructor(private api: ApiService,
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ){}
  colorMap: Map<string, string> = new Map();
  textMap: Map<string, string> = new Map();

  // Footer visibility state management
  private footerVisibilitySubject = new BehaviorSubject<boolean>(true);
  public footerVisibility$ = this.footerVisibilitySubject.asObservable();

  setFooterVisibility(visible: boolean): void {
    this.footerVisibilitySubject.next(visible);
  }

  getFooterVisibility(): boolean {
    return this.footerVisibilitySubject.value;
  }
    getInitials(fullname: string): string {
        if (!fullname || typeof fullname !== 'string') {
          return '';
        }
    
        // Check if the full name has spaces
        if (fullname.includes(' ')) {
          // if (words.length > 2) {
          //   const firstTwoWords = words.slice(0, 2);
          //   const initials = firstTwoWords.map(word => word.charAt(0)).join('');
          //   return initials.toUpperCase();
          // } else {
          //   const initials = words.map(word => word.charAt(0)).join('');
          //   return initials.toUpperCase();
          // }
          const words = fullname.split(' ');
          let initials = '';
          initials += words[0].charAt(0);
          const lastname = words[words.length - 1];
          initials += lastname.charAt(0);
          return initials.toUpperCase();
        } else {
          const initials = fullname.substr(0, 2);
          return initials.toUpperCase();
        }
      }
    
      getColor(username: string): string {
        if (!username) {
          return '';
        }
    
        let hash = 0;
        for (let i = 0; i < username.length; i++) {
          hash = username.charCodeAt(i) + ((hash << 5) - hash);
        }
    
        const r = (hash & 0xFF0000) >> 16;
        const g = (hash & 0x00FF00) >> 8;
        const b = hash & 0x0000FF;
    
        const pastelFactor = 253;    
        const pastelR = Math.floor((r + pastelFactor) / 2);
        const pastelG = Math.floor((g + pastelFactor) / 2);
        const pastelB = Math.floor((b + pastelFactor) / 2);
    
    
        const color = `#${((1 << 24) + (pastelR << 16) + (pastelG << 8) + pastelB).toString(16).slice(1).toUpperCase()}`;
    
    
        if (!this.colorMap.has(username)) {
          this.colorMap.set(username, color);
        }
    
        return this.colorMap.get(username) || '';
      }
    
      getInitialsparam(firstname: string | undefined, lastname: string | undefined): string {
        if (!firstname || !lastname) {
          return '';
        }
    
        if (firstname.includes(' ') || lastname.includes(' ')) {
          const fullname = firstname + ' ' + lastname;
          const words = fullname.split(' ');
          let initials = '';
          initials += words[0].charAt(0);
          const endingName = words[words.length - 1];
          initials += endingName.charAt(0);
          return initials.toUpperCase();
        } else {
          const initials = `${firstname.charAt(0)}${lastname.charAt(0)}`;
          return initials.toUpperCase();
        }
      }

      getTextColorParam(firstname: string | undefined, lastname: string | undefined): string {
        const fullName = `${firstname} ${lastname}`;
    
        let hash = 0;
        for (let i = 0; i < fullName.length; i++) {
          hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
        }
    
        const r = (hash & 0xFF0000) >> 16;
        const g = (hash & 0x00FF00) >> 8;
        const b = hash & 0x0000FF;
    

        const textPastelFactor = 100;
        const textPastelR = Math.floor((r + textPastelFactor) / 2);
        const textPastelG = Math.floor((g + textPastelFactor) / 2);
        const textPastelB = Math.floor((b + textPastelFactor) / 2);
    
    
        const textColor = `#${((1 << 24) + (textPastelR << 16) + (textPastelG << 8) + textPastelB).toString(16).slice(1).toUpperCase()}`;
    
        
        if(!this.textMap.has(fullName)) {
          this.textMap.set(fullName, textColor);
        }
        
        return this.textMap.get(fullName) || '';
      }
    
      getColorparam(firstname: string | undefined, lastname: string | undefined): string {
        const fullName = `${firstname} ${lastname}`;
    
        let hash = 0;
        for (let i = 0; i < fullName.length; i++) {
          hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
        }
    
        const r = (hash & 0xFF0000) >> 16;
        const g = (hash & 0x00FF00) >> 8;
        const b = hash & 0x0000FF;
    
        const pastelFactor = 255;    
        const pastelR = Math.floor(r * 0.2 + pastelFactor * 0.8);
        const pastelG = Math.floor(g * 0.2 + pastelFactor * 0.8);
        const pastelB = Math.floor(b * 0.2 + pastelFactor * 0.8);

    
    
        const color = `#${((1 << 24) + (pastelR << 16) + (pastelG << 8) + pastelB).toString(16).slice(1).toUpperCase()}`;
    
        if (!this.colorMap.has(fullName)) {
          this.colorMap.set(fullName, color);
        }
        
        return this.colorMap.get(fullName) || '';
    }

    // ===== New job-page UI helpers (presentational, no API) =====

    /** Maps a country code to a currency label. */
    getCurrency(country: string): string {
      switch (country) {
        case 'US': return 'USD';
        case 'IN': return 'INR';
        case 'CA': return 'CAD';
        case 'AU': return 'AUD';
        default: return 'CUR';
      }
    }

    /** Short pay-period suffix (hr / day / wk / mo / yr) for rate display. */
    getRatePeriod(payType: string): string {
      switch (payType) {
        case 'Hourly': return 'hr';
        case 'Daily': return 'day';
        case 'Weekly': return 'wk';
        case 'Monthly': return 'mo';
        case 'Yearly': return 'yr';
        default: return '';
      }
    }

    /** Builds "$75 – $95 USD / hr" (handles missing bounds gracefully). */
    formatRateRange(from: any, to: any, country: string, payType: string): string {
      const cur = this.getCurrency(country);
      const period = this.getRatePeriod(payType);
      const fmt = (v: any) => (v !== null && v !== undefined && `${v}` !== '' ? `$${v}` : '');
      const f = fmt(from);
      const t = fmt(to);
      const amount = (f && t) ? `${f} – ${t}` : (f || t);
      if (!amount) return '';
      return `${amount} ${cur}${period ? ' / ' + period : ''}`.trim();
    }

    /** Maps a job status to a CSS state used by the new badges. */
    getJobStatusClass(status: string): 'active' | 'paused' | 'closed' | 'default' {
      switch ((status || '').toUpperCase()) {
        case 'ACTIVE': return 'active';
        case 'ON HOLD':
        case 'INACTIVE':
        case 'DRAFTED': return 'paused';
        case 'CLOSED':
        case 'FILLED': return 'closed';
        default: return 'default';
      }
    }

    /** Candidate-side equivalent: status → CSS state for the new candidate badges. */
    getCandidateStatusClass(status: string): 'available' | 'unavailable' | 'draft' | 'blocked' | 'default' {
      switch ((status || '').toUpperCase()) {
        case 'ACTIVE': return 'available';
        case 'INACTIVE': return 'unavailable';
        case 'DRAFTED': return 'draft';
        case 'BLOCKED': return 'blocked';
        default: return 'default';
      }
    }

    /** Candidate-side equivalent: status → human label shown on badges. */
    getCandidateStatusLabel(status: string): string {
      switch ((status || '').toUpperCase()) {
        case 'ACTIVE': return 'Available';
        case 'INACTIVE': return 'Unavailable';
        case 'DRAFTED': return 'Draft';
        case 'BLOCKED': return 'Blocked';
        default: return status || '';
      }
    }

    // ===== SEO-friendly URL slug helpers =====
    // New URL pattern (per the SEO spec):
    //   Jobs    : /jobs/{title-slug}[-{city}-{state}]-{JID}
    //   Talents : /talents/{role-slug}[-{city}-{state}]-{CID}
    // Rules: lowercase, hyphens (not underscores, which Google treats as a join
    // character), location segments dropped when missing, ID preserved at the
    // end so the extractor regex still works.

    /** Lowercase, slugify a single part (e.g. "New York/NY!" → "new-york-ny"). */
    slugify(v: any): string {
      // strip diacritics via NFKD decomposition + combining-marks (U+0300–U+036F) removal,
      // then collapse non-alphanumerics to hyphens, dedupe, trim.
      return (v == null ? '' : `${v}`)
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    /** Joins parts into a single dash-separated slug, dropping empties. */
    joinSlug(...parts: any[]): string {
      return parts.map(p => this.slugify(p)).filter(Boolean).join('-');
    }

    /** Build the URL slug for a job: `title-[city-state-]jid`. */
    buildJobSlug(job: any): string {
      if (!job) return '';
      return this.joinSlug(job.jobTitle, job.city, job.state, job.jobId || job.id);
    }

    /** Build the URL slug for a candidate: `role-[city-state-]cid`. */
    buildCandidateSlug(candidate: any): string {
      if (!candidate) return '';
      return this.joinSlug(candidate.jobTitle, candidate.city, candidate.state, candidate.candidateId || candidate.id);
    }

    /** Extract the canonical job id (e.g. "JID12345", "JB-7876") from a route
     *  slug. Mirrors the listing's URL→id logic so other components (e.g. the
     *  sidebar) can resolve the selected job id without depending on it. */
    extractJobId(slug: string | null): string | null {
      if (!slug) return null;
      try {
        const decoded = decodeURIComponent(slug);
        // New slugs place the id in the trailing token ("…-jid12345"); legacy
        // URLs were mixed case. Match either, then upper-case to line up with
        // backend records.
        const trailing = decoded.split(/[-_]/).pop() || '';
        const jid = (trailing.match(/^jid\d+$/i) || decoded.match(/jid\d+/i) || [])[0];
        if (jid) return jid.toUpperCase();
        // Two-letter prefix + hyphen + digits (JB-…, MK-…, etc).
        const tail2 = decoded.split(/[-_]/).slice(-2).join('-');
        const dashed = (tail2.match(/^[a-z]{2,3}-\d+$/i) || decoded.match(/[a-z]{2,3}-\d+/i) || [])[0];
        if (dashed) return dashed.replace(/^([a-z]{2,3})/i, (m) => m.toUpperCase());
        // Pure-numeric id (legacy fallback).
        if (/^\d+$/.test(trailing)) return trailing;
        if (/^\d+$/.test(decoded)) return decoded;
        return null;
      } catch {
        return null;
      }
    }

    /** Extract the canonical candidate id (e.g. "CID12345", "MK-181") from a
     *  route slug. Candidate analog of {@link extractJobId}. */
    extractCandidateId(slug: string | null): string | null {
      if (!slug) return null;
      try {
        const decoded = decodeURIComponent(slug);
        const trailing = decoded.split(/[-_]/).pop() || '';
        const cid = (trailing.match(/^cid\d+$/i) || decoded.match(/cid\d+/i) || [])[0];
        if (cid) return cid.toUpperCase();
        const tail2 = decoded.split(/[-_]/).slice(-2).join('-');
        const dashed = (tail2.match(/^[a-z]{2,3}-\d+$/i) || decoded.match(/[a-z]{2,3}-\d+/i) || [])[0];
        if (dashed) return dashed.replace(/^([a-z]{2,3})/i, (m) => m.toUpperCase());
        if (/^\d+$/.test(trailing)) return trailing;
        if (/^\d+$/.test(decoded)) return decoded;
        return null;
      } catch {
        return null;
      }
    }

    async getFilterPayload(type: "JOB" | "CANDIDATE", userId: string) {
      try {
        const response = await lastValueFrom(this.api.query(`business/findNetworksForFilter?userId=${userId}&type=${type}`));
  
        if (response != null && response.code && response.code !== GigsumoConstants.SUCESSCODE) {
          throw new Error("There is some issue with Server");
        }
  
        return response;
      } catch (error) {
        console.error(error);
        throw error;
      }
    }

    
  static  FILTER_ITEMS(d = "JOB") {
    return d === "JOB" ?  [
      {
        value : "Management Talent Acquisition Network",
        key : "mtaNetworkJobs"
      },
      {
        value : "Freelance Recruiter Network",
        key : "freeLancerNetworkJobs"
      },
      {
        value : "Recruiter Network",
        key : "recruiterNetworkJobs"
      },
      {
        value : "Job Seeker Network",
        key : "jobSeekerNetworkCandidates"
      },
      {
        value : "Bench Sales Network",
        key : "benchNetworkCandidates"
      },
    ] :  [
      {
        value : "Management Talent Acquisition Network",
        key : "mtaNetworkCandidates"
      },
      {
        value : "Freelance Recruiter Network",
        key : "freeLancerNetworkCandidates"
      },
      {
        value : "Job Seeker Network",
        key : "jobSeekerNetworkCandidates"
      },
      {
        value : "Bench Sales Network",
        key : "benchNetworkCandidates"
      },
    ];
  }

  findJobsForOpenSession(u: any, User: any, errorMessage?: string): Observable<any> {

        // Check if data is already available in TransferState
        if (this.transferState.hasKey(JOBS_KEY)) {
          return of(this.transferState.get(JOBS_KEY, null));
        }

         // If on server, fetch and store in TransferState
    if (isPlatformServer(this.platformId)) {
      // return this.http.get('/api/findJobsForOpenSession').pipe(
      //   tap(data => {
      //     this.transferState.set(JOBS_KEY, data);
      //   })
      // );

      const url = AppSettings.ServerUrl + u;
      return this.http.post<any>(url, User).pipe(
        tap(data => {
          this.transferState.set(JOBS_KEY, data);
        }),
        catchError(this.handleError<any>(errorMessage))
      )
    }

    // If on client, check TransferState first
    const serverData = this.transferState.get(JOBS_KEY, null);
    if (serverData) {
      this.transferState.remove(JOBS_KEY);
      return of(serverData);
    }

    // Regular client-side fetch if no transfer state
    // return this.http.get('/api/findJobsForOpenSession');

      const url = AppSettings.ServerUrl + u;
      return this.http.post<any>(url, User);
    }

      public handleError<T>(operation: string | undefined, result?: T) {
        return (error: any): Observable<T> => {
          if (error.status === 500) {
            throw error;
          } else if (error.status === 0) {
            throw error;
          } else if (error.status === 400 || error.status === 401) {
            throw error;
          } else if (error.status === 404 || error.status === 440) {
            throw error;
          } else if (error.status === 303) {
            throw error;
          }
          return of(result as T);
        };
      }
}